import React, { useState } from "react";
import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import axios from "../../utils/axiosInstance";

export default function StripePaymentForm({ transaction, onPaymentConfirmed }) {
  const stripe = useStripe();
  const elements = useElements();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [waitingForWebhook, setWaitingForWebhook] = useState(false);

  const waitForServerConfirmation = async () => {
    for (let attempt = 0; attempt < 10; attempt += 1) {
      const response = await axios.get(`/api/transactions/${transaction.id}`);

      const current = response.data?.transaction || response.data;

      if (
        current.status === "validated" &&
        current.stripePaymentStatus === "succeeded"
      ) {
        onPaymentConfirmed(current);
        return true;
      }

      if (["cancelled", "failed"].includes(current.status)) {
        throw new Error(
          "Le serveur a refusé ou annulé cette transaction. Contactez le support.",
        );
      }

      await new Promise((resolve) => setTimeout(resolve, 1500));
    }

    return false;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || submitting) return;

    setSubmitting(true);
    setError("");
    setWaitingForWebhook(false);

    try {
      const result = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
      });

      if (result.error) {
        throw new Error(
          result.error.message || "Le paiement n'a pas pu être confirmé.",
        );
      }

      setWaitingForWebhook(true);

      const confirmed = await waitForServerConfirmation();

      if (!confirmed) {
        setError(
          "Stripe a reçu votre demande de paiement. La confirmation du serveur prend plus de temps que prévu. Ne recommencez pas le paiement ; actualisez le statut dans votre historique ou réessayez de vérifier plus tard.",
        );
      }
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err.message ||
          "Une erreur est survenue pendant le paiement.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-slate-900 dark:text-white">
          Paiement sécurisé
        </h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Transaction {transaction.paymentRef || transaction.id}
        </p>
      </div>

      <div className="rounded-2xl border border-black/10 dark:border-white/10 p-4 sm:p-5">
        <PaymentElement />
      </div>

      {waitingForWebhook && !error && (
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Paiement envoyé. Attente de la confirmation sécurisée du serveur…
        </p>
      )}

      {error && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-50 dark:bg-amber-900/20 p-4 text-sm text-amber-800 dark:text-amber-200">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || !elements || submitting}
        className="w-full rounded-2xl bg-slate-900 px-5 py-3 font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-slate-900"
      >
        {submitting ? "Vérification du paiement…" : "Payer maintenant"}
      </button>

      <p className="text-xs leading-5 text-slate-500 dark:text-slate-400">
        La transaction ne sera considérée comme payée dans l'application
        qu'après confirmation du serveur.
      </p>
    </form>
  );
}
